import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import WhatsAppButton from "@/components/WhatsAppButton";

const STATUS_LABEL = {
  agendado: { label: "Aberto", color: "#F59E0B", bg: "#FEF3C7" },
  confirmado: { label: "Confirmado s/ atend.", color: "#F59E0B", bg: "#FEF3C7" },
  cancelado: { label: "Cancelado", color: "#DC2626", bg: "#FEE2E2" },
  nao_compareceu: { label: "Não compareceu", color: "#7C2D12", bg: "#FED7AA" },
};

export default function FailedWeekCard({ apiCall }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [services, setServices] = useState([]);
  const [days, setDays] = useState("7");
  const [serviceId, setServiceId] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days });
      if (serviceId !== "all") params.append("service_id", serviceId);
      const [fRes, sRes] = await Promise.all([
        apiCall("get", `/appointments/failed-week?${params}`),
        apiCall("get", "/services"),
      ]);
      setItems(fRes.data || []);
      setServices(sRes.data || []);
    } catch {
      toast.error("Erro ao carregar falhas");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [days, serviceId]);

  const totals = useMemo(() => {
    const t = { open: 0, cancelled: 0, no_show: 0 };
    for (const a of items) {
      if (a.status === "agendado" || a.status === "confirmado") t.open += 1;
      else if (a.status === "cancelado") t.cancelled += 1;
      else if (a.status === "nao_compareceu") t.no_show += 1;
    }
    return t;
  }, [items]);

  return (
    <Card className="border border-border bg-card p-5 lg:p-6 shadow-soft rounded-md" data-testid="failed-week-card">
      <div className="flex items-center gap-2 mb-4">
        <AlertTriangle className="h-4 w-4" strokeWidth={1.5} style={{ color: "#DC2626" }} />
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-heading">
          Falhas da Semana
        </div>
        <span className="text-[10px] text-muted-foreground">(canceladas + faltas + pendentes não atendidos)</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <div>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="h-9 text-sm" data-testid="failed-period"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Últimos 7 dias</SelectItem>
              <SelectItem value="15">Últimos 15 dias</SelectItem>
              <SelectItem value="30">Últimos 30 dias</SelectItem>
              <SelectItem value="60">Últimos 60 dias</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Select value={serviceId} onValueChange={setServiceId}>
            <SelectTrigger className="h-9 text-sm" data-testid="failed-service"><SelectValue placeholder="Todos os serviços" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os serviços</SelectItem>
              {services.map((s) => (
                <SelectItem key={s.service_id} value={s.service_id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-3">
        <Stat label="Em aberto" value={totals.open} color="#F59E0B" />
        <Stat label="Cancelados" value={totals.cancelled} color="#DC2626" />
        <Stat label="Não compareceu" value={totals.no_show} color="#7C2D12" />
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground py-4">Carregando...</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center border border-dashed border-border rounded">
          Nenhuma falha no período selecionado ✨
        </div>
      ) : (
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {items.map((a) => {
            const s = STATUS_LABEL[a.status] || { label: a.status, color: "#64748B", bg: "#F1F5F9" };
            return (
              <div
                key={a.appointment_id}
                className="flex items-center gap-2 p-2 rounded-md hover:bg-secondary/60 transition-colors cursor-pointer"
                onClick={() => navigate("/pendentes")}
                data-testid={`failed-row-${a.appointment_id}`}
                role="button"
                tabIndex={0}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium truncate">{a.client_name}</span>
                    <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded" style={{ background: s.bg, color: s.color }}>{s.label}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {new Date(a.date).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                    {(a.services || []).length > 0 && ` · ${(a.services || []).map((sv) => sv.name).join(", ")}`}
                  </div>
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  <WhatsAppButton
                    toPhone={a.client_phone}
                    message={`Oi ${a.client_name}! Aqui é do salão Gregory João. Sentimos sua falta — podemos remarcar para uma data melhor para você? 💖`}
                    testId={`failed-whatsapp-${a.appointment_id}`}
                  />
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => { e.stopPropagation(); navigate("/pendentes"); }}
                  title="Ir para Pendentes"
                  data-testid={`failed-reschedule-${a.appointment_id}`}
                >
                  <RotateCw className="h-4 w-4" strokeWidth={1.5} />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="p-2 rounded-md border border-border bg-card text-center">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-xl font-light" style={{ color }}>{value}</div>
    </div>
  );
}
